// SaleCartManager.js - Fixed version

import { getData } from './ApiSettings';
import { getReferencedFlag } from './user';

// Custom event for sales cart updates
export const SALE_CART_UPDATE_EVENT = 'saleCartUpdate';

// Default fallback when the API setting hasn't been fetched yet (or fails).
// Stays a hard fallback so the UI never breaks; the real value lives in
// the `general-settings` content-type and is loaded asynchronously.
const DEFAULT_FREE_DELIVERY_THRESHOLD = 59;
let cachedThreshold = null;

async function loadFreeDeliveryThreshold() {
  if (cachedThreshold != null) return cachedThreshold;
  try {
    const response = await getData('/general-setting');
    const value = response?.data?.data?.free_delivery_threshold;
    if (value != null) {
      cachedThreshold = parseFloat(value);
      return cachedThreshold;
    }
  } catch (err) {
    // 404 = not yet configured, fall through to default
    console.warn('General settings not loaded, using default delivery threshold');
  }
  cachedThreshold = DEFAULT_FREE_DELIVERY_THRESHOLD;
  return cachedThreshold;
}

// Eagerly start fetching at module load so the value is available quickly.
loadFreeDeliveryThreshold();

export class SaleCartManager {
  static CART_KEY = 'sale_cart';

  static notifyCartUpdate() {
    // Dispatch a custom event when cart is updated
    window.dispatchEvent(new CustomEvent(SALE_CART_UPDATE_EVENT));
  }

  static getCart() {
    try {
      const cart = localStorage.getItem(this.CART_KEY);
      return cart ? JSON.parse(cart) : [];
    } catch (error) {
      console.error('Error getting sale cart:', error);
      return [];
    }
  }

  static saveCart(cart) {
    try {
      localStorage.setItem(this.CART_KEY, JSON.stringify(cart));
      this.notifyCartUpdate();
    } catch (error) {
      console.error('Error saving sale cart:', error);
    }
  }

  static addToCart(product, quantity = 1) {
    try {
      const cart = this.getCart();
      const existingItemIndex = cart.findIndex(item => 
        item.product.documentId === product.documentId
      );

      if (existingItemIndex > -1) {
        const newQuantity = cart[existingItemIndex].quantity + quantity;
        cart[existingItemIndex].quantity = newQuantity;
        // Update price based on new quantity
        cart[existingItemIndex].price = this.getCurrentPrice(product, newQuantity);
      } else {
        const currentPrice = this.getCurrentPrice(product, quantity);
        cart.push({
          product,
          quantity,
          price: currentPrice,
          addedAt: new Date().toISOString()
        });
      }

      this.saveCart(cart);
      return true;
    } catch (error) {
      console.error('Error adding to sale cart:', error);
      return false;
    }
  }

  static removeFromCart(productDocumentId) {
    try {
      const cart = this.getCart();
      const updatedCart = cart.filter(item => 
        item.product.documentId !== productDocumentId
      );
      this.saveCart(updatedCart);
      return updatedCart;
    } catch (error) {
      console.error('Error removing from sale cart:', error);
      return this.getCart();
    }
  }

  static updateQuantity(productDocumentId, quantity) {
    try {
      const cart = this.getCart();
      const itemIndex = cart.findIndex(item => 
        item.product.documentId === productDocumentId
      );

      if (itemIndex > -1) {
        if (quantity <= 0) {
          cart.splice(itemIndex, 1);
        } else {
          cart[itemIndex].quantity = quantity;
          // Update price based on new quantity
          cart[itemIndex].price = this.getCurrentPrice(cart[itemIndex].product, quantity);
        }
        this.saveCart(cart);
      }
      return cart;
    } catch (error) {
      console.error('Error updating sale cart quantity:', error);
      return this.getCart();
    }
  }

  static getTotalPrice() {
    const cart = this.getCart();
    return cart.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  }

  static getItemCount() {
    const cart = this.getCart();
    return cart.reduce((count, item) => count + item.quantity, 0);
  }

  // Helper method to determine current price based on quantity.
  // Priority: bulk_price (if threshold met) > private_price (if private offer
  // exists AND user is referenced) > unit_price.
  static getCurrentPrice(product, quantity) {
    if (!product) return 0;

    // Bulk pricing wins when quantity threshold is met
    if (product.bulk_price && product.bulk_quantity && quantity >= product.bulk_quantity) {
      return product.bulk_price;
    }

    // Tarif vente privée : appliqué UNIQUEMENT si le visiteur est référencé
    // (a déjà passé une commande). Le flag est synchronisé via
    // helpers/user.checkIsReferenced(), lui-même appelé au mount des pages
    // qui en ont besoin.
    const hasPrivateOffer =
      (product.is_private === true || product.sale_category?.is_private === true) &&
      product.private_price != null;
    if (hasPrivateOffer && getReferencedFlag()) {
      return product.private_price;
    }

    return product.unit_price;
  }

  // Helper method to check if bulk pricing is applied
  static isBulkPriceApplied(product, quantity) {
    return product.bulk_price && product.bulk_quantity && quantity >= product.bulk_quantity;
  }

  static clearCart() {
    try {
      localStorage.removeItem(this.CART_KEY);
      this.notifyCartUpdate();
      return true;
    } catch (error) {
      console.error('Error clearing sale cart:', error);
      return false;
    }
  }

  static getDeliveryThreshold() {
    return cachedThreshold ?? DEFAULT_FREE_DELIVERY_THRESHOLD;
  }

  static async getDeliveryThresholdAsync() {
    return loadFreeDeliveryThreshold();
  }

  static isEligibleForFreeDelivery() {
    return this.getTotalPrice() >= this.getDeliveryThreshold();
  }
}